/**
 * Local SEO for tenant websites — pure helpers shared by the SPA (PageSeo,
 * LocalBusinessJsonLd, the admin "Google & SEO" check) and the Vercel API routes
 * (api/tenant-page.ts server-rendered meta, api/tenant-seo.ts robots/sitemap).
 *
 * KEEP THIS FILE DEPENDENCY-FREE (no imports). The Vercel functions import it
 * as `../src/lib/localSeo.js`; with "type": "module" Vercel resolves imports with
 * strict ESM rules, so anything this file imported would also need an explicit
 * extension and to be traced into the function bundle.
 */

export const PLATFORM_ROOT_DOMAIN = 'thepolishedversion.com';

/** Google shows roughly this many characters of a title / description. */
export const TITLE_MAX = 60;
export const DESCRIPTION_MAX = 155;

// ─── Input shapes (subset of the DB rows) ──────────────────────────────────────

export type SocialLinkValue = string | null | undefined | { enabled?: boolean | null; url?: string | null };

export interface SeoCompany {
  company_name?: string | null;
  logo_url?: string | null;
  phone_number?: string | null;
  service_areas?: string[] | null;
  social_links?: Record<string, SocialLinkValue> | null;
  currency_code?: string | null;
  footer_tagline?: string | null;
  updated_at?: string | null;
}

export interface SeoService {
  name: string;
  description?: string | null;
  price?: number | string | null;
  is_quote_only?: boolean | null;
  category?: string | null;
  sort_order?: number | null;
  updated_at?: string | null;
}

export interface SeoHours {
  day_of_week: number;
  is_open: boolean;
  open_time?: string | null;
  close_time?: string | null;
}

export interface SeoPageRow {
  page_slug: string;
  seo_title?: string | null;
  meta_description?: string | null;
  indexing_preference?: string | null;
  canonical_url?: string | null;
  updated_at?: string | null;
}

export interface SeoCustomPage {
  slug: string;
  title?: string | null;
  meta_description?: string | null;
  is_published?: boolean | null;
  updated_at?: string | null;
}

export type BuiltInPage = 'home' | 'services' | 'about' | 'contact' | 'booking';
export type SeoPage = { kind: BuiltInPage } | { kind: 'custom'; slug: string; title?: string | null; description?: string | null };

export const BUILT_IN_PATHS: Record<BuiltInPage, string> = {
  home: '/',
  services: '/services',
  about: '/about',
  contact: '/contact',
  booking: '/booking',
};

// ─── Text helpers ──────────────────────────────────────────────────────────────

const clean = (value: string | null | undefined) => String(value ?? '').replace(/\s+/g, ' ').trim();

/**
 * Shorten to `max` characters on a word boundary, with an ellipsis. Text that
 * already fits is returned unchanged (whitespace-normalised).
 */
export function truncateAtWord(text: string, max: number): string {
  const value = clean(text);
  if (value.length <= max) return value;
  const room = value.slice(0, Math.max(1, max - 1));
  const lastSpace = room.lastIndexOf(' ');
  const cut = lastSpace > max * 0.5 ? room.slice(0, lastSpace) : room;
  return `${cut.replace(/[\s,;:.&|–—-]+$/, '')}…`;
}

/** "A" · "A & B" · "A, B & C". */
export function joinAnd(items: string[]): string {
  const list = items.map(clean).filter(Boolean);
  if (list.length <= 1) return list[0] ?? '';
  return `${list.slice(0, -1).join(', ')} & ${list[list.length - 1]}`;
}

function uniqueCaseless(items: string[]): string[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = item.toLowerCase();
    if (!item || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Named service areas, de-duplicated, in the owner's order. */
export function serviceAreas(company: SeoCompany | null | undefined): string[] {
  return uniqueCaseless((company?.service_areas ?? []).map(clean));
}

/** "End of Tenancy Cleaning" → "End of Tenancy"; "Luxury Home Clean" → "Luxury Home". */
export function serviceLabel(name: string): string {
  const value = clean(name);
  const stripped = value.replace(/\s+(?:cleaning|cleans?|services?)$/i, '').trim();
  return stripped || value;
}

/** Active services in display order (sort_order, then as given). */
export function topServices(services: SeoService[] | null | undefined): SeoService[] {
  return [...(services ?? [])]
    .filter((s) => clean(s.name))
    .map((s, index) => ({ s, index }))
    .sort((a, b) => (a.s.sort_order ?? 0) - (b.s.sort_order ?? 0) || a.index - b.index)
    .map(({ s }) => s);
}

function topLabels(services: SeoService[] | null | undefined, count: number): string[] {
  return uniqueCaseless(topServices(services).map((s) => serviceLabel(s.name))).slice(0, count);
}

const CURRENCY_SYMBOLS: Record<string, string> = { GBP: '£', EUR: '€', USD: '$', AUD: 'A$', NZD: 'NZ$', CAD: 'C$' };
const CURRENCY_COUNTRIES: Record<string, string> = { GBP: 'GB', USD: 'US', AUD: 'AU', NZD: 'NZ', CAD: 'CA' };

export function currencyCode(company: SeoCompany | null | undefined): string {
  return clean(company?.currency_code).toUpperCase() || 'GBP';
}

function servicePrice(service: SeoService): number | null {
  if (service.is_quote_only) return null;
  const price = typeof service.price === 'number' ? service.price : Number.parseFloat(String(service.price ?? ''));
  return Number.isFinite(price) && price > 0 ? price : null;
}

/**
 * Text the platform seeds into new accounts as a prompt for the owner. It isn't
 * a real description, so it's never repeated in structured data, llms.txt or
 * the crawler copy of a page.
 */
const PLACEHOLDER_TEXT = [/edit the details from the admin/i];

export function isPlaceholderText(value: string | null | undefined): boolean {
  const text = clean(value);
  return !text || PLACEHOLDER_TEXT.some((re) => re.test(text));
}

/** The owner's service description, or '' when it's empty / still the seeded placeholder. */
export function serviceDescription(service: SeoService): string {
  return isPlaceholderText(service.description) ? '' : clean(service.description);
}

/**
 * The price label the Services page shows: "From £60" · "Request a quote". A
 * fixed-price service with no price set gets '' (we don't state a price).
 */
export function servicePriceLabel(service: SeoService, company: SeoCompany | null | undefined): string {
  if (service.is_quote_only) return 'Request a quote';
  const price = servicePrice(service);
  return price === null ? '' : `From ${formatMoney(price, currencyCode(company))}`;
}

export function formatMoney(amount: number, code: string): string {
  const symbol = CURRENCY_SYMBOLS[code] ?? `${code} `;
  return `${symbol}${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
}

/** Lowest bookable price, e.g. "£25" — null when every service is quote-only. */
export function fromPrice(services: SeoService[] | null | undefined, company: SeoCompany | null | undefined): string | null {
  const prices = (services ?? []).map(servicePrice).filter((p): p is number => p !== null);
  return prices.length ? formatMoney(Math.min(...prices), currencyCode(company)) : null;
}

/** schema.org priceRange, e.g. "£25-£130" (Google: a numeric range under 100 characters). */
export function priceRange(services: SeoService[] | null | undefined, company: SeoCompany | null | undefined): string | null {
  const prices = (services ?? []).map(servicePrice).filter((p): p is number => p !== null);
  if (!prices.length) return null;
  const code = currencyCode(company);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? formatMoney(min, code) : `${formatMoney(min, code)}-${formatMoney(max, code)}`;
}

/** First candidate that fits, else the last one shortened on a word boundary. */
function firstFitting(candidates: string[], max: number): string {
  const list = uniqueCaseless(candidates.map(clean).filter(Boolean));
  return list.find((c) => c.length <= max) ?? truncateAtWord(list[list.length - 1] ?? '', max);
}

/** Join sentences, dropping trailing optional ones that don't fit. */
function fitSentences(required: string, optional: string[], max: number): string {
  let text = clean(required);
  if (text.length > max) return truncateAtWord(text, max);
  for (const sentence of optional.map(clean).filter(Boolean)) {
    const next = `${text} ${sentence}`;
    if (next.length <= max) text = next;
  }
  return text;
}

// ─── Smart default titles & descriptions ───────────────────────────────────────

export interface SeoContext {
  businessName: string;
  company?: SeoCompany | null;
  services?: SeoService[] | null;
}

/** "Domestic & End of Tenancy Cleaning" · "Cleaning Services" when there are none. */
function servicesPhrase(services: SeoService[] | null | undefined, count: number): string {
  const labels = topLabels(services, count);
  return labels.length ? `${joinAnd(labels)} Cleaning` : 'Cleaning Services';
}

/**
 * The title Google shows when the owner hasn't written one, ≤ 60 characters.
 * e.g. "Domestic & End of Tenancy Cleaning in Cardiff & Newport | Sparkle & Co".
 * Richest variant that fits wins; the business name is dropped last.
 */
export function defaultSeoTitle(page: SeoPage, ctx: SeoContext): string {
  const brand = clean(ctx.businessName);
  const areas = serviceAreas(ctx.company);
  const in2 = areas.length ? ` in ${joinAnd(areas.slice(0, 2))}` : '';
  const in1 = areas.length ? ` in ${areas[0]}` : '';
  const withBrand = (lead: string) => (brand ? `${lead} | ${brand}` : lead);
  const brandOnly = brand || 'Cleaning Services';

  switch (page.kind) {
    case 'home':
      return firstFitting([
        withBrand(`${servicesPhrase(ctx.services, 2)}${in2}`),
        withBrand(`${servicesPhrase(ctx.services, 2)}${in1}`),
        withBrand(`${servicesPhrase(ctx.services, 1)}${in2}`),
        withBrand(`${servicesPhrase(ctx.services, 1)}${in1}`),
        withBrand(`Cleaning Services${in1}`),
        withBrand('Cleaning Services'),
        brandOnly,
      ], TITLE_MAX);
    case 'services':
      return firstFitting([
        withBrand(`Cleaning Services & Prices${in2}`),
        withBrand(`Cleaning Services & Prices${in1}`),
        withBrand(`Services & Prices${in1}`),
        withBrand('Services & Prices'),
        brandOnly,
      ], TITLE_MAX);
    case 'about':
      return firstFitting([
        withBrand(`About Us${areas.length ? ` – Local Cleaners${in1}` : ''}`),
        withBrand('About Us'),
        brandOnly,
      ], TITLE_MAX);
    case 'contact':
      return firstFitting([
        withBrand(`Contact Us${areas.length ? ` – Cleaning${in1}` : ''}`),
        withBrand('Contact Us'),
        brandOnly,
      ], TITLE_MAX);
    case 'booking':
      return firstFitting([
        withBrand(`Book a Clean Online${in1}`),
        withBrand('Book a Clean Online'),
        brandOnly,
      ], TITLE_MAX);
    case 'custom': {
      const title = clean(page.title) || 'Page';
      return firstFitting([withBrand(title), title], TITLE_MAX);
    }
  }
}

/** The meta description Google shows when the owner hasn't written one, ≤ 155 characters. */
export function defaultSeoDescription(page: SeoPage, ctx: SeoContext): string {
  const brand = clean(ctx.businessName) || 'We';
  const areas = serviceAreas(ctx.company);
  const areaText = joinAnd(areas.slice(0, 3));
  const where = areaText ? ` in ${areaText}` : '';
  const labels = topLabels(ctx.services, 3);
  const what = labels.length ? `${joinAnd(labels)} cleaning` : 'professional cleaning';
  const from = fromPrice(ctx.services, ctx.company);
  const phone = clean(ctx.company?.phone_number);

  switch (page.kind) {
    case 'home':
      return fitSentences(
        `${brand} offers ${what}${where}.`,
        [from ? `Prices from ${from}.` : '', 'Book online in minutes.', 'Friendly, reliable local cleaners.'],
        DESCRIPTION_MAX,
      );
    case 'services': {
      const priced = topServices(ctx.services)
        .slice(0, 3)
        .map((s) => {
          const price = servicePrice(s);
          return price !== null ? `${clean(s.name)} from ${formatMoney(price, currencyCode(ctx.company))}` : clean(s.name);
        });
      const lead = priced.length ? `${joinAnd(priced)}.` : `See ${brand === 'We' ? 'our' : `${brand}'s`} cleaning services and prices.`;
      return fitSentences(lead, [`See every service and book online${where}.`, `See prices and book online.`], DESCRIPTION_MAX);
    }
    case 'about':
      return fitSentences(
        brand === 'We' ? `A local cleaning company${where ? ` serving ${areaText}` : ''}.` : `Meet ${brand}, a local cleaning company${areaText ? ` serving ${areaText}` : ''}.`,
        [labels.length ? `We specialise in ${what}.` : '', 'Find out how we work and why customers choose us.'],
        DESCRIPTION_MAX,
      );
    case 'contact':
      return fitSentences(
        `Get in touch with ${brand === 'We' ? 'us' : brand} for cleaning${where}.`,
        [phone ? `Call ${phone} or send us a message.` : 'Send us a message and we will get back to you.', 'Free, no-obligation quotes.'],
        DESCRIPTION_MAX,
      );
    case 'booking':
      return fitSentences(
        `Book ${what}${brand === 'We' ? '' : ` with ${brand}`} online.`,
        ['Pick a date and time that suits you.', from ? `Prices from ${from}.` : 'See the price before you book.', areaText ? `Covering ${areaText}.` : ''],
        DESCRIPTION_MAX,
      );
    case 'custom': {
      const own = clean(page.description);
      if (own) return truncateAtWord(own, DESCRIPTION_MAX);
      const title = clean(page.title) || 'More information';
      return fitSentences(`${title} – ${brand === 'We' ? 'our cleaning company' : brand}${where}.`, ['Book online in minutes.'], DESCRIPTION_MAX);
    }
  }
}

/**
 * Brand an owner-written (or default) title the way PageSeo always has: swap any
 * stale org/signup names for the current business name, drop duplicate parts and
 * make sure the business name appears once.
 */
export function brandTitle(value: string, businessName: string, staleNames: string[] = [], fallback = ''): string {
  const brand = clean(businessName);
  const replaced = replaceStale(value, brand, staleNames);
  const parts = replaced.split('|').map((p) => p.trim()).filter(Boolean);
  const brandIndex = brand ? parts.indexOf(brand) : -1;
  // Title has no current business name but several parts: the last part is an
  // old brand suffix, so drop it before appending the current name.
  const kept = brandIndex === -1 && brand && parts.length > 1 ? parts.slice(0, -1) : parts;
  const deduped = kept.filter((part, index) => kept.indexOf(part) === index);
  const normalized = deduped.join(' | ') || brand || fallback;
  if (!brand) return normalized;
  return normalized.includes(brand) ? normalized : `${normalized} | ${brand}`;
}

/** Make sure a description mentions the business (after replacing stale names). */
export function brandDescription(value: string, businessName: string, staleNames: string[] = [], fallback = ''): string {
  const brand = clean(businessName);
  const branded = replaceStale(value, brand, staleNames);
  if (!brand || branded.includes(brand)) return branded;
  const fallbackBranded = fallback ? replaceStale(fallback, brand, staleNames) : '';
  if (fallbackBranded.includes(brand)) return fallbackBranded;
  return fallbackBranded ? `${brand}: ${fallbackBranded}` : brand;
}

function replaceStale(value: string, brand: string, staleNames: string[]): string {
  if (!brand) return clean(value);
  return staleNames
    .filter((name) => name && name !== brand && !brand.includes(name))
    .reduce((current, stale) => current.split(stale).join(brand), clean(value))
    .replace(/\s*\|\s*$/, '')
    .replace(/^\s*\|\s*/, '')
    .trim();
}

/** Final title + description for a page: the owner's text when set, else the smart default. */
export function resolvePageSeo(
  page: SeoPage,
  ctx: SeoContext & { row?: SeoPageRow | null; staleNames?: string[] },
): { title: string; description: string; noindex: boolean; canonical: string | null; usingDefaults: { title: boolean; description: boolean } } {
  const ownTitle = clean(ctx.row?.seo_title);
  const ownDescription = clean(ctx.row?.meta_description);
  const defaultTitle = defaultSeoTitle(page, ctx);
  const defaultDescription = defaultSeoDescription(page, ctx);
  return {
    title: ownTitle ? brandTitle(ownTitle, ctx.businessName, ctx.staleNames, defaultTitle) : defaultTitle,
    description: ownDescription ? brandDescription(ownDescription, ctx.businessName, ctx.staleNames, defaultDescription) : defaultDescription,
    noindex: ctx.row?.indexing_preference === 'noindex',
    canonical: clean(ctx.row?.canonical_url) || null,
    usingDefaults: { title: !ownTitle, description: !ownDescription },
  };
}

// ─── Hosts & URLs ──────────────────────────────────────────────────────────────

/** Path → which tenant page it is (null for app/admin/etc. pages we don't describe). */
export function seoPageFromPath(path: string): SeoPage | null {
  const p = `/${String(path ?? '').split(/[?#]/)[0]}`.replace(/\/+/g, '/').replace(/(.)\/$/, '$1').toLowerCase();
  if (p === '/' || /^\/org\/[^/]+$/.test(p)) return { kind: 'home' };
  const builtIn = (Object.keys(BUILT_IN_PATHS) as BuiltInPage[]).find((k) => BUILT_IN_PATHS[k] === p);
  if (builtIn) return { kind: builtIn };
  const custom = p.match(/^\/p\/([a-z0-9-]+)$/);
  return custom ? { kind: 'custom', slug: custom[1] } : null;
}

/** page_seo_settings.page_slug for a page. */
export function pageSlugFor(page: SeoPage): string {
  return page.kind === 'custom' ? page.slug : page.kind;
}

export function pathFor(page: SeoPage): string {
  return page.kind === 'custom' ? `/p/${page.slug}` : BUILT_IN_PATHS[page.kind];
}

/**
 * The tenant's canonical origin: their own domain once it is live (verified AND
 * serving over HTTPS), else their subdomain on our platform.
 */
export function tenantOrigin(slug: string, customDomain?: string | null): string {
  const domain = clean(customDomain).toLowerCase().replace(/^www\./, '');
  return domain ? `https://${domain}` : `https://${clean(slug).toLowerCase()}.${PLATFORM_ROOT_DOMAIN}`;
}

/** A custom_domains row is live (safe to canonicalise to) when verified and TLS is active. */
export function liveCustomDomain(rows: Array<{ domain?: string | null; verified_at?: string | null; tls_status?: string | null }> | null | undefined): string | null {
  const row = (rows ?? []).find((r) => r.domain && r.verified_at && r.tls_status === 'active');
  return row?.domain ? row.domain.toLowerCase() : null;
}

// ─── JSON-LD (schema.org LocalBusiness) ────────────────────────────────────────

const SOCIAL_KEYS_FOR_SAME_AS = ['facebook', 'instagram', 'linkedin', 'tiktok', 'twitter', 'x', 'youtube', 'pinterest'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function httpUrl(value: string | null | undefined): string | null {
  const v = clean(value);
  if (!v) return null;
  const withScheme = /^https?:\/\//i.test(v) ? v : /^[\w-]+(\.[\w-]+)+(\/|$)/.test(v) ? `https://${v}` : '';
  try {
    return withScheme ? new URL(withScheme).toString() : null;
  } catch {
    return null;
  }
}

/** Enabled social profile URLs (WhatsApp is a chat link, not a profile, so it's left out). */
export function sameAsLinks(socialLinks: Record<string, SocialLinkValue> | null | undefined): string[] {
  if (!socialLinks || typeof socialLinks !== 'object') return [];
  const urls = SOCIAL_KEYS_FOR_SAME_AS.map((key) => {
    const value = socialLinks[key];
    if (!value) return null;
    if (typeof value === 'string') return httpUrl(value);
    return value.enabled === false ? null : httpUrl(value.url);
  }).filter((u): u is string => Boolean(u));
  return [...new Set(urls)];
}

const hhmm = (t: string | null | undefined) => clean(t).slice(0, 5);

export interface LocalBusinessInput {
  origin: string;
  businessName: string;
  company?: SeoCompany | null;
  services?: SeoService[] | null;
  hours?: SeoHours[] | null;
  description?: string | null;
  image?: string | null;
}

/**
 * schema.org LocalBusiness for a tenant's homepage.
 *
 * Type: schema.org has no cleaning-specific LocalBusiness subtype; the closest is
 * HomeAndConstructionBusiness ("a business that provides services around homes
 * and buildings"). Google asks for the most specific subtype and does NOT
 * support additionalType, so we use that subtype on its own.
 *
 * Deliberately NO aggregateRating / review: Google treats ratings a business
 * shows about itself (including embedded Google reviews) as self-serving and
 * ineligible, and says not to aggregate reviews from other sites.
 */
export function buildLocalBusinessJsonLd(input: LocalBusinessInput): Record<string, unknown> {
  const origin = input.origin.replace(/\/+$/, '');
  const company = input.company ?? null;
  const name = clean(input.businessName) || clean(company?.company_name);
  const logo = httpUrl(company?.logo_url);
  const image = httpUrl(input.image) || logo;
  const telephone = clean(company?.phone_number);
  const areas = serviceAreas(company);
  const sameAs = sameAsLinks(company?.social_links);
  const code = currencyCode(company);
  const country = CURRENCY_COUNTRIES[code];
  const range = priceRange(input.services, company);

  const offers = topServices(input.services).map((service) => {
    const price = servicePrice(service);
    const description = serviceDescription(service);
    return {
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: clean(service.name),
        ...(description ? { description: truncateAtWord(description, 300) } : {}),
        ...(areas.length ? { areaServed: areas } : {}),
      },
      ...(price !== null
        ? {
            price: price.toFixed(2),
            priceCurrency: code,
            // The site shows "From £x": a starting price, not a fixed one.
            priceSpecification: { '@type': 'PriceSpecification', minPrice: price.toFixed(2), priceCurrency: code },
          }
        : {}),
      url: `${origin}/booking`,
    };
  });

  const hours = (input.hours ?? [])
    .filter((h) => h.is_open && hhmm(h.open_time) && hhmm(h.close_time) && DAY_NAMES[h.day_of_week])
    .sort((a, b) => ((a.day_of_week + 6) % 7) - ((b.day_of_week + 6) % 7))
    .map((h) => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: `https://schema.org/${DAY_NAMES[h.day_of_week]}`,
      opens: hhmm(h.open_time),
      closes: hhmm(h.close_time),
    }));

  const description = clean(input.description);

  return {
    '@context': 'https://schema.org',
    '@type': 'HomeAndConstructionBusiness',
    '@id': `${origin}/#business`,
    name,
    url: `${origin}/`,
    ...(description ? { description } : {}),
    ...(logo ? { logo } : {}),
    ...(image ? { image } : {}),
    ...(telephone ? { telephone } : {}),
    // We only store the country (no street address is collected), so that's all we state.
    ...(country ? { address: { '@type': 'PostalAddress', addressCountry: country } } : {}),
    ...(areas.length ? { areaServed: areas.map((area) => ({ '@type': 'Place', name: area })) } : {}),
    ...(sameAs.length ? { sameAs } : {}),
    ...(range ? { priceRange: range } : {}),
    ...(hours.length ? { openingHoursSpecification: hours } : {}),
    ...(offers.length
      ? { hasOfferCatalog: { '@type': 'OfferCatalog', name: 'Cleaning services', itemListElement: offers } }
      : {}),
  };
}

/** JSON for a <script type="application/ld+json"> body — `<` escaped so text can't close the tag. */
export function serializeJsonLd(data: unknown): string {
  return JSON.stringify(data)
    .replace(/</g, '\\u003c')
    .split(String.fromCharCode(0x2028)).join('\\u2028')
    .split(String.fromCharCode(0x2029)).join('\\u2029');
}

// ─── robots.txt & sitemap.xml ──────────────────────────────────────────────────

/**
 * Search engines and AI search / answer engines named in robots.txt. The
 * `User-agent: *` group already lets everyone else in; naming these makes the
 * welcome explicit. Each named group repeats the private-path rules, because a
 * crawler that finds a group for its own name ignores the `*` group entirely.
 */
export const SEARCH_AND_AI_CRAWLERS = [
  'Googlebot',
  'Google-Extended',
  'Bingbot',
  'Applebot',
  'Applebot-Extended',
  'DuckDuckBot',
  'DuckAssistBot',
  'OAI-SearchBot',
  'ChatGPT-User',
  'GPTBot',
  'PerplexityBot',
  'Perplexity-User',
  'ClaudeBot',
  'Claude-SearchBot',
  'Claude-User',
] as const;

/** Private app paths on tenant sites — never useful in search results or AI answers. */
export const TENANT_DISALLOWED_PATHS = ['/admin', '/auth', '/customer', '/quote/'];

export function buildRobotsTxt(origin: string): string {
  const base = origin.replace(/\/+$/, '');
  const rules = ['Allow: /', ...TENANT_DISALLOWED_PATHS.map((path) => `Disallow: ${path}`)];
  return [
    '# Search engines and AI search / answer engines are welcome here.',
    ...SEARCH_AND_AI_CRAWLERS.map((bot) => `User-agent: ${bot}`),
    ...rules,
    '',
    'User-agent: *',
    ...rules,
    '',
    `Sitemap: ${base}/sitemap.xml`,
    '',
  ].join('\n');
}

/**
 * robots.txt for our own marketing site (served as the static public/robots.txt;
 * a test keeps the two identical). Everything stays open, as before.
 */
export function buildPlatformRobotsTxt(): string {
  return [
    '# Search engines, AI search / answer engines and link previews are welcome here.',
    ...SEARCH_AND_AI_CRAWLERS.map((bot) => `User-agent: ${bot}`),
    'User-agent: Twitterbot',
    'User-agent: facebookexternalhit',
    'Allow: /',
    '',
    'User-agent: *',
    'Allow: /',
    '',
    `Sitemap: https://www.${PLATFORM_ROOT_DOMAIN}/sitemap.xml`,
    '',
  ].join('\n');
}

export interface SitemapEntry {
  loc: string;
  lastmod?: string | null;
}

function latest(...dates: Array<string | null | undefined>): string | null {
  const times = dates.map((d) => (d ? Date.parse(d) : Number.NaN)).filter((t) => Number.isFinite(t));
  return times.length ? new Date(Math.max(...times)).toISOString() : null;
}

export interface SitemapInput {
  origin: string;
  seoRows?: SeoPageRow[] | null;
  customPages?: SeoCustomPage[] | null;
  /** updated_at sources for the built-in pages. */
  updated?: {
    company?: string | null;
    services?: Array<string | null | undefined>;
    homepageContent?: string | null;
    aboutContent?: string | null;
  };
}

/**
 * Public, indexable tenant pages: home, services, about, contact, booking and
 * published custom pages. A page the owner set to "noindex" — or pointed at a
 * different canonical URL — is left out (sitemaps should list canonical URLs only).
 */
export function sitemapEntries(input: SitemapInput): SitemapEntry[] {
  const origin = input.origin.replace(/\/+$/, '');
  const rows = new Map((input.seoRows ?? []).map((row) => [row.page_slug, row]));
  const u = input.updated ?? {};
  const servicesUpdated = latest(...(u.services ?? []));

  const pages: Array<{ page: SeoPage; lastmod: string | null }> = [
    { page: { kind: 'home' }, lastmod: latest(u.company, u.homepageContent, servicesUpdated) },
    { page: { kind: 'services' }, lastmod: latest(servicesUpdated, u.company) },
    { page: { kind: 'about' }, lastmod: latest(u.aboutContent, u.company) },
    { page: { kind: 'contact' }, lastmod: latest(u.company) },
    { page: { kind: 'booking' }, lastmod: latest(servicesUpdated) },
    ...(input.customPages ?? [])
      .filter((p) => p.is_published !== false && /^[a-z0-9-]+$/.test(p.slug ?? ''))
      .map((p) => ({ page: { kind: 'custom', slug: p.slug } as SeoPage, lastmod: latest(p.updated_at) })),
  ];

  const entries: SitemapEntry[] = [];
  for (const { page, lastmod } of pages) {
    const row = rows.get(pageSlugFor(page));
    if (row?.indexing_preference === 'noindex') continue;
    const loc = `${origin}${pathFor(page)}`;
    const canonical = clean(row?.canonical_url).replace(/\/+$/, '');
    if (canonical && canonical !== loc.replace(/\/+$/, '')) continue;
    entries.push({ loc, lastmod: latest(lastmod, row?.updated_at) });
  }
  return entries;
}

const xmlEscape = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');

export function buildSitemapXml(entries: SitemapEntry[]): string {
  const urls = entries
    .map((e) => `  <url>\n    <loc>${xmlEscape(e.loc)}</loc>${e.lastmod ? `\n    <lastmod>${xmlEscape(e.lastmod)}</lastmod>` : ''}\n  </url>`)
    .join('\n');
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}${urls ? '\n' : ''}</urlset>\n`;
}

// ─── Language ──────────────────────────────────────────────────────────────────

const CURRENCY_LANGS: Record<string, string> = { GBP: 'en-GB', USD: 'en-US', AUD: 'en-AU', NZD: 'en-NZ', CAD: 'en-CA' };

/** <html lang> for a tenant site: from their currency's country, British English by default. */
export function htmlLang(company: SeoCompany | null | undefined): string {
  return CURRENCY_LANGS[currencyCode(company)] ?? 'en-GB';
}

// ─── More JSON-LD: WebSite & BreadcrumbList ────────────────────────────────────

/** schema.org WebSite for a tenant's homepage (Google uses it for the site name in results). */
export function buildWebSiteJsonLd(input: { origin: string; businessName: string; lang?: string }): Record<string, unknown> {
  const origin = input.origin.replace(/\/+$/, '');
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${origin}/#website`,
    name: clean(input.businessName),
    url: `${origin}/`,
    inLanguage: input.lang ?? 'en-GB',
    publisher: { '@id': `${origin}/#business` },
  };
}

/** Human name of a tenant page, as its nav link / heading calls it. */
export function pageLabel(page: SeoPage): string {
  switch (page.kind) {
    case 'home': return 'Home';
    case 'services': return 'Services';
    case 'about': return 'About';
    case 'contact': return 'Contact';
    case 'booking': return 'Book online';
    case 'custom': return clean(page.title) || 'Page';
  }
}

/** schema.org BreadcrumbList: Home › this page. null on the homepage (nothing to show). */
export function buildBreadcrumbJsonLd(input: { origin: string; page: SeoPage }): Record<string, unknown> | null {
  if (input.page.kind === 'home') return null;
  const origin = input.origin.replace(/\/+$/, '');
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
      { '@type': 'ListItem', position: 2, name: pageLabel(input.page), item: `${origin}${pathFor(input.page)}` },
    ],
  };
}

// ─── Crawler-readable page copy & llms.txt ─────────────────────────────────────

/** Everything public about a tenant that the crawler copy and llms.txt are built from. */
export interface TenantSiteContent {
  origin: string;
  businessName: string;
  company?: SeoCompany | null;
  services?: SeoService[] | null;
  hours?: SeoHours[] | null;
  /** The owner's own one-line description (organizations.description / tagline). */
  summary?: string | null;
  /** The homepage hero the owner saved (never the built-in default copy). */
  hero?: { title?: string | null; subtitle?: string | null } | null;
  /** The About page the owner saved (page_content about/all). */
  about?: {
    hero?: { title?: string | null; subtitle?: string | null } | null;
    values?: Array<{ title?: string | null; description?: string | null }> | null;
  } | null;
  /** Published custom pages that are open to search (title + slug). */
  customPages?: Array<{ slug: string; title?: string | null }> | null;
  /** page_seo_settings slugs the owner set to noindex — left out of llms.txt. */
  noindexPages?: string[] | null;
}

export function escapeHtml(value: string | null | undefined): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Opening hours in Monday-first order, e.g. "Monday: 08:00–18:00", "Sunday: Closed". */
export function openingHoursLines(hours: SeoHours[] | null | undefined): string[] {
  return [...(hours ?? [])]
    .filter((h) => DAY_NAMES[h.day_of_week])
    .sort((a, b) => ((a.day_of_week + 6) % 7) - ((b.day_of_week + 6) % 7))
    .map((h) => {
      const open = h.is_open && hhmm(h.open_time) && hhmm(h.close_time);
      return `${DAY_NAMES[h.day_of_week]}: ${open ? `${hhmm(h.open_time)}–${hhmm(h.close_time)}` : 'Closed'}`;
    });
}

function telHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}

/**
 * Plain, semantic HTML of a tenant page's key facts, put inside #root by
 * api/tenant-page.ts so crawlers that don't run JavaScript (most AI search and
 * answer engines) can read the page.
 *
 * It only repeats what the live page itself shows — business name, the saved
 * hero/about copy, services with their "From £x" prices, areas, phone and
 * opening hours — so people and crawlers get the same content (no cloaking).
 * React replaces it as soon as the app mounts (createRoot clears the
 * container), and index.html hides it while JavaScript is running so the
 * page never flashes.
 */
export function buildTenantPrerenderHtml(page: SeoPage, site: TenantSiteContent): string {
  const name = clean(site.businessName);
  const company = site.company ?? null;
  const services = topServices(site.services);
  const areas = serviceAreas(company);
  const phone = clean(company?.phone_number);
  const hours = openingHoursLines(site.hours);
  const e = escapeHtml;

  const nav = (Object.keys(BUILT_IN_PATHS) as BuiltInPage[])
    .map((kind) => `<a href="${BUILT_IN_PATHS[kind]}">${e(pageLabel({ kind }))}</a>`)
    .join(' ');

  const serviceList = services.length
    ? `<ul>${services
        .map((s) => {
          const price = servicePriceLabel(s, company);
          const description = serviceDescription(s);
          return `<li><h3>${e(clean(s.name))}</h3>${price ? `<p>${e(price)}</p>` : ''}${description ? `<p>${e(description)}</p>` : ''}</li>`;
        })
        .join('')}</ul>`
    : '';
  const servicesSection = (heading: string) =>
    serviceList ? `<section><h2>${e(heading)}</h2>${serviceList}<p><a href="/booking">Book online</a></p></section>` : '';
  const areasSection = areas.length ? `<section><h2>Areas we cover</h2><p>${e(joinAnd(areas))}</p></section>` : '';
  const phoneLine = phone ? `<p>Phone: <a href="${e(telHref(phone))}">${e(phone)}</a></p>` : '';
  const hoursSection = hours.length ? `<section><h2>Opening hours</h2><ul>${hours.map((h) => `<li>${e(h)}</li>`).join('')}</ul></section>` : '';
  const contactSection = phone || hours.length
    ? `<section><h2>Contact ${e(name)}</h2>${phoneLine}<p><a href="/contact">Send a message</a> or <a href="/booking">book online</a>.</p></section>${hoursSection}`
    : '';

  let main: string;
  switch (page.kind) {
    case 'home': {
      const heroTitle = clean(site.hero?.title);
      const heroSubtitle = clean(site.hero?.subtitle);
      main = [
        `<h1>${e(heroTitle || name)}</h1>`,
        heroTitle && heroTitle !== name ? `<p>${e(name)}</p>` : '',
        heroSubtitle ? `<p>${e(heroSubtitle)}</p>` : '',
        servicesSection('Services'),
        areasSection,
        contactSection,
      ].join('');
      break;
    }
    case 'services':
      main = [`<h1>${e(`Services & prices – ${name}`)}</h1>`, servicesSection('Our services'), areasSection].join('');
      break;
    case 'booking':
      main = [`<h1>${e(`Book online with ${name}`)}</h1>`, '<p>Choose a service, then pick a date and time.</p>', servicesSection('Services'), areasSection].join('');
      break;
    case 'about': {
      const title = clean(site.about?.hero?.title);
      const subtitle = clean(site.about?.hero?.subtitle);
      const values = (site.about?.values ?? [])
        .map((v) => ({ title: clean(v.title), description: clean(v.description) }))
        .filter((v) => v.title);
      main = [
        `<h1>${e(title || `About ${name}`)}</h1>`,
        subtitle ? `<p>${e(subtitle)}</p>` : '',
        values.length
          ? `<section><h2>What we stand for</h2><ul>${values.map((v) => `<li><h3>${e(v.title)}</h3>${v.description ? `<p>${e(v.description)}</p>` : ''}</li>`).join('')}</ul></section>`
          : '',
        areasSection,
      ].join('');
      break;
    }
    case 'contact':
      main = [`<h1>${e(`Contact ${name}`)}</h1>`, phoneLine, '<p><a href="/booking">Book online</a></p>', areasSection, hoursSection].join('');
      break;
    case 'custom': {
      const description = clean(page.description);
      main = [`<h1>${e(clean(page.title) || name)}</h1>`, description ? `<p>${e(description)}</p>` : ''].join('');
      break;
    }
  }

  const tagline = clean(company?.footer_tagline);
  const footer = [
    `<p>${e(name)}${tagline ? ` – ${e(tagline)}` : ''}</p>`,
    page.kind !== 'contact' && page.kind !== 'home' && phone ? phoneLine : '',
  ].join('');

  return `<div data-tpv-prerender="tenant"><header><p><a href="/">${e(name)}</a></p><nav aria-label="Main">${nav}</nav></header><main>${main}</main><footer>${footer}</footer></div>`;
}

/**
 * /llms.txt for a tenant (https://llmstxt.org): a short Markdown brief an AI
 * assistant can read to answer "who are they, what do they do, where, for how
 * much and how do I book". Built only from the owner's own data.
 */
export function buildTenantLlmsTxt(site: TenantSiteContent): string {
  const origin = site.origin.replace(/\/+$/, '');
  const name = clean(site.businessName);
  const company = site.company ?? null;
  const services = topServices(site.services);
  const areas = serviceAreas(company);
  const phone = clean(company?.phone_number);
  const hours = openingHoursLines(site.hours);
  const from = fromPrice(services, company);
  const hidden = new Set(site.noindexPages ?? []);
  const labels = topLabels(services, 3);

  const intro = `${name} is a cleaning company${labels.length ? ` offering ${joinAnd(labels)} cleaning` : ''}${areas.length ? ` in ${joinAnd(areas.slice(0, 6))}` : ''}.`;
  const ownSummary = clean(site.summary);
  const lines: string[] = [`# ${name}`, '', `> ${intro}${hidden.has('booking') ? '' : ' Customers can book online.'}`, ''];
  if (ownSummary && !isPlaceholderText(ownSummary)) lines.push(ownSummary, '');

  const facts = [
    `- Website: ${origin}/`,
    areas.length ? `- Areas covered: ${areas.join(', ')}` : '',
    from ? `- Prices: from ${from} (${currencyCode(company)})` : '',
    phone ? `- Phone: ${phone}` : '',
    hours.length ? `- Opening hours: ${hours.join('; ')}` : '',
  ].filter(Boolean);
  lines.push(...facts, '');

  if (services.length) {
    lines.push('## Services', '');
    for (const s of services) {
      const price = servicePriceLabel(s, company);
      const description = serviceDescription(s);
      const notes = [price, description].filter(Boolean).join('. ');
      lines.push(`- [${clean(s.name)}](${origin}/services)${notes ? `: ${notes}` : ''}`);
    }
    lines.push('');
  }

  const pageLinks: Array<[SeoPage, string]> = [
    [{ kind: 'booking' }, 'choose a service, then pick a date and time'],
    [{ kind: 'contact' }, phone ? `send a message or call ${phone}` : 'send a message'],
    [{ kind: 'services' }, 'every service with its price'],
    [{ kind: 'about' }, `about ${name}`],
  ];
  const visible = pageLinks.filter(([page]) => !hidden.has(pageSlugFor(page)));
  if (visible.length) {
    lines.push('## Book or get in touch', '');
    for (const [page, note] of visible) lines.push(`- [${pageLabel(page)}](${origin}${pathFor(page)}): ${note}`);
    lines.push('');
  }

  const custom = (site.customPages ?? []).filter((p) => /^[a-z0-9-]+$/.test(p.slug ?? '') && !hidden.has(p.slug));
  if (custom.length) {
    lines.push('## More pages', '');
    for (const p of custom) lines.push(`- [${clean(p.title) || p.slug}](${origin}/p/${p.slug})`);
    lines.push('');
  }

  return `${lines.join('\n').replace(/\n{3,}/g, '\n\n').trimEnd()}\n`;
}
